/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment, useState } from 'react';
import { RouteComponentProps, useLocation, navigate } from '@reach/router';
import { Box, Divider, Typography } from '@material-ui/core';

import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import ROUTES from '../../../../../routes';
import colors from '../../../../../themes/main-colors';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';
import { getFormattedCurrencyString } from '../../../../../util/format';
import { PgeEvModel } from '../../../../pge-plus-common/PgeEvModel';

const EligibleForRebates: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const [isPGEEvModelVisible, setIsPGEEvModelVisible] = useState<boolean>(
    false,
  );
  const { content, isContentLoading, richText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_REBATE_CONFIRM,
  );
  const {
    content: contentEvRebatesFlow,
    isContentLoading: isContentEvRebatesFlowLoading,
    richText: richTextEvRebatesFlow,
  } = useContentMgmt(microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG);

  const location = useLocation();
  const locationState: any = location.state;

  const mainContent =
    locationState?.rebateType === 'standard' ? (
      richTextEvRebatesFlow(
        contentEvRebatesFlow.get(
          'PGE_PLUS_EV_REBATES_FLOW_ELIGIBILITY_TEXT_STANDARD',
        ),
        {
          CHARGER_AMOUNT: getFormattedCurrencyString(
            locationState?.chargerAmount || '',
          ),
          PANEL_UPGRADE: getFormattedCurrencyString(
            locationState?.panelUpgradeAmount || '',
          ),
        },
      )
    ) : locationState?.rebateType === 'higher' ? (
      <Fragment>
        {richText(content.get('REBATE_ONLY_ELIGIBILITY_TEXT_HIGHER_1'), {
          CHARGER_AMOUNT: getFormattedCurrencyString(
            locationState?.chargerAmount || '',
          ),
          PANEL_UPGRADE: getFormattedCurrencyString(
            locationState?.panelUpgradeAmount || '',
          ),
        })}
      </Fragment>
    ) : (
      <Fragment>
        {richText(content.get('REBATE_ONLY_ELIGIBILITY_TEXT_IQBD_1'), {
          CHARGER_AMOUNT: getFormattedCurrencyString(
            locationState?.chargerAmount || '',
          ),
          PANEL_UPGRADE: getFormattedCurrencyString(
            locationState?.panelUpgradeAmount || '',
          ),
        })}
      </Fragment>
    );

  const handleSubmit = (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    void navigate(ROUTES.PGE_PLUS_REBATE_ONLY_CHARGER_INFORMATION, {
      state: {
        backButtonRoute:
          locationState?.rebateType === 'standard'
            ? ROUTES.PGE_PLUS_REBATE_ONLY_STANDARD_REBATES_ELIGIBILE
            : ROUTES.PGE_PLUS_REBATE_ONLY_HIGHER_REBATES_ELIGIBILE,
      },
    });
  };

  const handleSaveAndExit = () => {
    void navigate(ROUTES.ACCOUNT);
  };

  if (isContentLoading || isContentEvRebatesFlowLoading) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <Typography
          css={{
            fontFamily: 'Untitled-Sans',
            fontSize: 20,
            color: colors.noirBlur,
            marginBottom: '26px',
            marginTop: '20px',
            display: 'inline-block',
            '& p:first-child': {
              marginTop: 0,
            },
            '& p:last-child': {
              marginBottom: 0,
            },
            '& a': { textDecoration: 'none' },
          }}
        >
          {mainContent}
        </Typography>
        <Divider css={{ color: colors.gray93, margin: '20px 0 20px 0' }} />
        <ProceedOrCancelButton
          back={
            locationState?.isAbleToRequestIQBD
              ? ROUTES.PGE_PLUS_REBATE_ONLY_HIGHER_REBATES_CHECK
              : ROUTES.PGE_PLUS_REBATE_ONLY_CUSTOMER_INFORMATION
          }
          backLabel={content.get('REUSABLE_BUTTON_BACK')}
        />
      </Box>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
      <PgeEvModel
        isVisible={isPGEEvModelVisible}
        setIsVisible={setIsPGEEvModelVisible}
      />
    </form>
  );
};

export default EligibleForRebates;
